import React, { useEffect, useRef, useState } from "react"
import tw from "twin.macro"
import { useRouter } from "next/router"
import { Routes } from "../../routes"
import { ReactComponent as SearchSVG } from "../../assets/profile/search.svg"
import { ReactComponent as CrossSVG } from "../../assets/profile/cross.svg"
import {
  QuestionSearchResult,
  SearchResultEntityType,
  SourceCommentSearchResult,
  useProfileSearchLazyQuery,
} from "../../graphql/generated/graphql"
import { useTranslation } from "next-i18next"
import { useClickOutside } from "../../utils/hooks/useClickOutside"
import { Dropdown } from "./dropdown"
import { GetQuestionsProps, QuestionSort } from "./questions"

interface SearchProp {
  getQuestions: ({ platform, page, sort, videoId }: GetQuestionsProps) => Promise<void>
  username: string
}
export const Search = ({ getQuestions, username }: SearchProp) => {
  const { t } = useTranslation("profile")
  const router = useRouter()
  const [selected, setSelected] = useState(false)
  const [input, setInput] = useState("")
  const [focus, setFocus] = useState(-1)
  const [hasFocus, setHasFocus] = useState(false)
  const [questions, setQuestions] = useState<QuestionSearchResult[]>([])
  const [videos, setVideos] = useState<SourceCommentSearchResult[]>([])
  const [profileSearchQuery, { data: searchData }] = useProfileSearchLazyQuery()
  const response = searchData && searchData.ProfileSearch.results

  const clear = () => {
    setQuestions([])
    setVideos([])
    setFocus(-1)
    setHasFocus(false)
    setSelected(false)
  }
  const handleFocus = () => {
    setHasFocus(!hasFocus)
  }

  const ref = useRef(null)
  useClickOutside(ref, () => clear())
  interface Typeahead {
    input: string
    timeout: undefined | ReturnType<typeof setTimeout>
  }

  const [typeahead, setTypeahead] = useState<Typeahead>({ input: "", timeout: undefined })
  const questionSearch = (input: string) => {
    if (typeahead.timeout) {
      clearTimeout(typeahead.timeout)
    }
    setTypeahead({
      input,
      timeout: setTimeout(async () => await profileSearchQuery({ variables: { substring: input, username } }), 300),
    })
  }

  useEffect(() => {
    if (response) {
      setQuestions(
        response.filter(
          (result: Record<string, any>) => result.entityType === SearchResultEntityType.Question,
        ) as QuestionSearchResult[],
      )
      setVideos(
        response.filter(
          (result: Record<string, any>) => result.entityType === SearchResultEntityType.Sourcecomment,
        ) as SourceCommentSearchResult[],
      )
    }
    if (!input && hasFocus) {
      handleFocus()
    }
  }, [response, input])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    clear()
    setInput(event.target.value)
  }

  const handleSelect = async (id: string, value?: string, route?: string) => {
    value && setInput(value)
    setSelected(true)
    if (route) {
      await router.push({ pathname: route, query: { id } })
    } else {
      await getQuestions({ page: 1, sort: QuestionSort.Popular, videoId: id })
    }
    clear()
  }

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 40) {
      setFocus(focus + 1)
    }
    if (event.keyCode === 38) {
      setFocus(focus - 1)
    }
    if (event.keyCode === 13) {
      event.preventDefault()
      const results: Record<string, any>[] = [...videos, ...questions]
      if (focus > -1) {
        switch (results[focus].entityType) {
          case SearchResultEntityType.Question:
            return await handleSelect(results[focus].id, input, Routes.ShowQuestion)
          default:
            return await handleSelect(results[focus].videoId, input)
        }
      }
    }
  }

  const dropdownProps = {
    videos,
    questions,
    focus,
    setFocus,
    handleSelect,
    input,
    selected,
  }
  return (
    <form tw="w-full relative flex items-center mb-3 tablet-lg:(mr-3 mb-0)" ref={ref}>
      <div tw="w-full flex items-center bg-white rounded-full border-2 border-base-3 pl-14">
        <input
          tw="w-[80%] h-[40px] px-3 text-md bg-transparent text-base-6 font-medium focus:outline-none overflow-hidden whitespace-nowrap overflow-ellipsis text-base-6"
          css={`&::placeholder {${tw`text-base-5`}`}
          data-testid={`${name}-input`}
          type="text"
          name="question-search"
          id={`${name}-id`}
          onFocus={() => {
            !input && handleFocus()
          }}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyUp={() => input && questionSearch(input)}
          value={input}
          placeholder={t("search.title")}
          aria-label={input}
        />
      </div>
      {input && <CrossSVG tw="absolute right-10 hover:cursor-pointer" onClick={() => setInput("")} />}
      <Dropdown {...dropdownProps} />
      <SearchSVG data-testid="search-icon" tw="absolute left-10" />
    </form>
  )
}
